import { HttpClient, HttpParams, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import * as sha512 from 'js-sha512';

import { catchError, finalize, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Client } from "../domain/client";
import { Contact } from "../domain/contact";
import { EUserRole } from "../domain/enum/user-role";
import { Notification } from "../domain/notification";
import { Prevision } from "../domain/prevision";
import { User } from "../domain/user";
import { CoreData } from "../tools/core-data/core-data";
import { LoadingTask } from "../tools/ui/loading-task";
import { BaseService } from "./base-service";
import { AuthentificationDto } from "./DTO/authentification-dto";

@Injectable({
    providedIn: 'root',
})
export class UserService extends BaseService {

    private serviceUrl = `${environment.serviceBaseUrl}api/apave`;

    constructor(public http: HttpClient,
        protected coreData: CoreData,
        protected snackBare: MatSnackBar,
        private loadingTask: LoadingTask,
        protected router: Router) {

        super(router, coreData, snackBare);
    }

    getAllUsers() {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/All`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getAllUsers')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: User[]) => {
                return response.map((item: User) => {
                    return User.mapFromJSON(item);
                });
            }));
    }

    addClientToUser(client: Client, user: User) {
        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/${user.Email}/Client`, JSON.stringify(client), this.generateHeader()).pipe(
            catchError(this.handleError<any>('addClientToUser')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    // deleteClientFromUser(client: Client, user: User) {
    //     this.loadingTask.activateLoading();
    //     return this.http.delete(`${this.serviceUrl}/${user.Email}/${client.Code}`, this.generateHeaderWithBearer()).pipe(
    //         catchError(this.handleError<any>('deleteClientFromUser')),
    //         finalize(() => this.loadingTask.deactivateLoading()),
    //         map((response: boolean) => {
    //             return response;
    //         }));
    // }

    logAs(email: string) {
        const dto: AuthentificationDto = new AuthentificationDto();
        dto.Email = email;
        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/AuthenticationAs`, JSON.stringify(dto), this.generateHeader()).pipe(
            catchError(this.handleError<any>('logAs')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: User) => {
                return User.mapFromJSON(response);
            }));
    }

    createUser(user: User) {

        this.loadingTask.activateLoading();

        return this.http.post(`${this.serviceUrl}`, JSON.stringify(user), this.generateHeader()).pipe(
            catchError(this.handleError<any>('createUser')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    updateUser(user: User) {

        this.loadingTask.activateLoading();
        return this.http.put(`${this.serviceUrl}`, JSON.stringify(user), this.generateHeader()).pipe(
            catchError(this.handleError<any>('updateUser')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    updateUserWithStatusCode(user: User) {
        this.loadingTask.activateLoading();
        return this.http.request(new HttpRequest('PUT', `${this.serviceUrl}`, JSON.stringify(user), this.generateHeader()));
    }

    deleteUser(user: User) {
        this.loadingTask.activateLoading();
        return this.http.delete(`${this.serviceUrl}/${encodeURI(user.Email)}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('deleteUser')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    createContact(userId: number | undefined, contactCode: string) {

        this.loadingTask.activateLoading();

        return this.http.post(`${this.serviceUrl}/Contact/${userId}/${contactCode}`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('createContact')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    deleteContact(userId: number | undefined, contactCode: string) {

        this.loadingTask.activateLoading();

        return this.http.delete(`${this.serviceUrl}/Contact/${userId}/${contactCode}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('deleteContact')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    tryCredential(user: User) {
        const dto: AuthentificationDto = new AuthentificationDto();
        dto.ClientKey = this.getFingerPrintGuid();
        dto.Email = user.Email;
        dto.Password = sha512.sha512(user.Password);
        const body = JSON.stringify(dto);
        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/Authentication`, body, this.generateHeader()).pipe(
            catchError(this.handleError<any>('tryCredential')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }

    askForResetPassword(email: string) {

        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/${encodeURI(email)}/passwordChangeRequest`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('askForResetPassword')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    resetPassword(email: string, key: string, password: string) {
        const dto = {
            newPassword: sha512.sha512(password),
            email: email,
            key: key
        };

        const body = JSON.stringify(dto);

        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/passwordReset`, body, this.generateHeader()).pipe(
            catchError(this.handleError<any>('resetPassword')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return User.mapFromJSON(response);
            }));
    }

    /**
 * 
 * @param text Client Code or Client Name
 * @returns 
 */
    getClientList(text: string) {
        let params = new HttpParams();
        if (text) {
            params = params.append('filter', `${text}`);
        }
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/elimsClient`, this.generateHeaderWithParameters(params)).pipe(
            catchError(this.handleError<any>('getClientList')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Client[]) => {
                return response.map((item: Client) => {
                    return Client.mapFromJSON(item);
                });
            }));
    }

    // getClienByCode(code: string) {

    //     this.loadingTask.activateLoading();
    //     return this.http.get(`${this.serviceUrl}/${code}`, this.generateHeader()).pipe(
    //         catchError(this.handleError<any>('getClienByCode')),
    //         finalize(() => this.loadingTask.deactivateLoading()),
    //         map((response: Client) => {
    //             return Client.mapFromJSON(response);
    //         }));
    // }

    // getDefaultClient() {
    //     this.loadingTask.activateLoading();
    //     return this.http.get(`${this.serviceUrl}/default`, this.generateHeader()).pipe(
    //         catchError(this.handleError<any>('getDefaultClient')),
    //         finalize(() => this.loadingTask.deactivateLoading()),
    //         map((response: Client) => {
    //             return response ? Client.mapFromJSON(response) : null;
    //         }));
    // }

    getContactList(text: string) {
        let params = new HttpParams();
        if (text) {
            params = params.append('filter', `${text}`);
        }
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/elimsContact`, this.generateHeaderWithParameters(params)).pipe(
            catchError(this.handleError<any>('getContactList')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Contact[]) => {
                return response.map((item: Contact) => {
                    return Contact.mapFromJSON(item);
                });
            }));
    }

    getCalendarURL() {
        this.loadingTask.activateLoading();

        return this.http.get(`${this.serviceUrl}/CalendarURL`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getCalendarURL')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }

    getMyPrevisions() {
        this.loadingTask.activateLoading();

        return this.http.get(`${this.serviceUrl}/previsions`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getMyPrevisions')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Prevision) => {
                return Prevision.mapFromJSON(response);
            }));
    }

    changePrevisions(prevision: Prevision) {
        this.loadingTask.activateLoading();

        return this.http.put(`${this.serviceUrl}/previsions`, JSON.stringify(prevision), this.generateHeader()).pipe(
            catchError(this.handleError<any>('changePrevisions')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    getUserNotifications() {
        this.loadingTask.activateLoading();

        return this.http.get(`${this.serviceUrl}/notifications`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getUserNotifications')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Notification) => {
                return Notification.mapFromJSON(response);
            }));
    }

    changeUserNotifications(notif: Notification) {
        this.loadingTask.activateLoading();

        return this.http.put(`${this.serviceUrl}/notifications`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('changeUserNotifications')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    activeUnactiveUser(email: string) {

        return this.http.put(`${this.serviceUrl}/${encodeURI(email)}/isactive`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('activeUnactiveUser')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }
}